import { tz } from '@date-fns/tz'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNFormDate from 'components/KNForm/KNFormDate'
import KNFormDropdown from 'components/KNForm/KNFormDropdown'
import KNFormText from 'components/KNForm/KNFormText'
import KNFormTimeNext from 'components/KNForm/KNFormTimeNext'
import { parseISO } from 'date-fns'
import { processDefaultValues } from 'global/helpers/form'
import { getTimezoneOptions } from 'global/helpers/timezones'
import { validateEmail } from 'global/helpers/validators'
import { useEffect, useState } from 'react'
import { flushSync } from 'react-dom'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { findDayValue } from 'screens/QuoteDetails/CarrierQuoteAnswerSummary'
import { isQuoteOfferAnswer } from 'screens/QuoteDetails/QuoteDetails.service'
import {
  type QuoteAnswer,
  type QuoteAnswerFormValues,
  type QuoteOffer,
  type QuoteRejection,
  type QuoteRequest,
  QuoteStatus,
} from 'screens/QuoteDetails/QuoteDetails.types'
import { quoteAnswerQueryOptions, QUOTES_QUERY_KEY, sendQuoteAnswer } from 'screens/Quotes/Quotes.service'

export interface CarrierQuoteFormProps {
  quoteRequest: QuoteRequest
  quoteAnswer?: QuoteAnswer
  onAction: (answer: QuoteAnswer) => void
}

const buildContact = (name?: string, email?: string) => name && email ? { name, email } : undefined

const buildComments = (comments?: string) => comments?.trim() ? comments : undefined

const buildOfferReference = (ref?: string) => ref?.trim() ? ref : undefined

const buildExpirationDate = (data: QuoteAnswerFormValues) =>
  parseISO(`${data.quoteExpirationDate}T${data.quoteExpirationTime}`, { in: tz(data.quoteExpirationTimezone) }).toISOString()

const CarrierQuoteForm = ({
  quoteRequest,
  quoteAnswer,
  onAction,
}: CarrierQuoteFormProps) => {
  const { t } = useTranslation()
  const { handleSubmit, reset, control } = useForm<QuoteAnswerFormValues>()
  const [pendingAction, setPendingAction] = useState<QuoteStatus | null>(null)
  const queryClient = useQueryClient()
  const sendQuoteAnswerMutation = useMutation({
    mutationFn: (answer: QuoteAnswer) => sendQuoteAnswer(quoteRequest.token, answer),
    onSuccess: (answer) => {
      queryClient.setQueryData(quoteAnswerQueryOptions(quoteRequest.token).queryKey, answer)
      return queryClient.invalidateQueries({ queryKey: [QUOTES_QUERY_KEY] })
    },
  })

  useEffect(() => {
    const baseValues = {
      carrierQuoteReference: quoteAnswer?.offerReference,
      quoteExpirationTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      comments: quoteAnswer?.comments,
      carrierContact: quoteAnswer?.contact?.name,
      carrierContactEmail: quoteAnswer?.contact?.email,
    }
    if (quoteAnswer && isQuoteOfferAnswer(quoteAnswer)) {
      reset(
        processDefaultValues({
          ...baseValues,
          quoteAmount: quoteAnswer.amount,
          currency: quoteAnswer.currency,
          quoteExpirationDate: quoteAnswer.expirationDate && new Date(quoteAnswer.expirationDate),
          quoteExpirationTime: quoteAnswer.expirationDate && new Date(quoteAnswer.expirationDate),
          quoteExpirationTimezone: quoteAnswer.expirationDateTimezone,
          days: quoteAnswer.transitDuration && Number(findDayValue(quoteAnswer.transitDuration)),
        })
      )
    } else {
      reset(processDefaultValues(baseValues))
    }
  }, [quoteAnswer])

  const buildOffer = (data: QuoteAnswerFormValues): QuoteOffer => ({
    status: QuoteStatus.SUBMITTED,
    quoteReference: quoteRequest.quoteReference,
    version: quoteRequest.version,
    contact: buildContact(data.carrierContact, data.carrierContactEmail),
    expirationDate: buildExpirationDate(data),
    expirationDateTimezone: data.quoteExpirationTimezone,
    amount: data.quoteAmount.toString(),
    currency: data.currency,
    transitDuration: `P${data.days}D`,
    comments: buildComments(data.comments),
    offerReference: buildOfferReference(data.carrierQuoteReference),
  })

  const buildRejection = (data: QuoteAnswerFormValues): QuoteRejection => ({
    status: QuoteStatus.REJECTED,
    quoteReference: quoteRequest.quoteReference,
    version: quoteRequest.version,
    comments: buildComments(data.comments),
    contact: buildContact(data.carrierContact, data.carrierContactEmail),
  })

  const submitAnswer = async (answer: QuoteAnswer): Promise<void> => {
    try {
      const result = await sendQuoteAnswerMutation.mutateAsync(answer)
      onAction(result)
    } catch (error) {
      console.log(error)
    }
  }

  const onSubmit: SubmitHandler<QuoteAnswerFormValues> = (data) => submitAnswer(buildOffer(data))
  const onReject: SubmitHandler<QuoteAnswerFormValues> = (data) => submitAnswer(buildRejection(data))

  return (
    <Grid container spacing={4}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Stack direction="column" spacing={1}>
          <Typography variant="p4" color="primary.main">
            {t('screens.cs.carrier_quotes.quote_offer.quote')}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 8 }}>
              <KNFormText
                name="quoteAmount"
                type="number"
                label={t('screens.cs.carrier_quotes.quote_offer.my_quote')}
                control={control}
                rules={{
                  required: pendingAction === QuoteStatus.REJECTED ? false : t('form.validation.required'),
                  min: {
                    value: 1,
                    message: t('form.validation.min_number', { min: 1 }),
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <KNFormDropdown
                name="currency"
                label={t('screens.cs.carrier_quotes.quote_offer.currency')}
                control={control}
                options={Object.values(quoteRequest.acceptedCurrencies).map((currency) => ({
                  value: currency,
                  label: currency,
                }))}
                rules={{
                  required: pendingAction === QuoteStatus.REJECTED ? false : t('form.validation.required'),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <KNFormText
                name="carrierQuoteReference"
                label={t('screens.cs.carrier_quotes.quote_offer.my_reference')}
                control={control}
              />
            </Grid>
          </Grid>
          <Typography variant="p6" color="primary.main">
            {t('screens.cs.carrier_quotes.quote_offer.my_expiry')}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 4 }}>
              <KNFormDate
                name="quoteExpirationDate"
                label={t('screens.cs.carrier_quotes.quote_offer.validityDate')}
                control={control}
                rules={{
                  required: pendingAction === QuoteStatus.REJECTED ? false : t('form.validation.required'),
                }}
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <KNFormTimeNext
                name="quoteExpirationTime"
                label={t('screens.cs.carrier_quotes.quote_offer.validityTime')}
                control={control}
                rules={{
                  required: pendingAction === QuoteStatus.REJECTED ? false : t('form.validation.required'),
                }}
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <KNFormDropdown
                name="quoteExpirationTimezone"
                label={t('screens.cs.carrier_quotes.quote_offer.validityTimezone')}
                control={control}
                rules={{
                  required: pendingAction === QuoteStatus.REJECTED ? false : t('form.validation.required'),
                }}
                options={getTimezoneOptions()}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid size={{ xs: 4 }}>
              <KNFormText
                name="days"
                type="number"
                label={t('screens.cs.carrier_quotes.quote_offer.transit_duration')}
                control={control}
                rules={{
                  required: pendingAction === QuoteStatus.REJECTED ? false : t('form.validation.required'),
                  min: {
                    value: 1,
                    message: t('form.validation.min_number', { min: 1 }),
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <KNFormText
                name="comments"
                label={t('screens.cs.carrier_quotes.quote_offer.comments')}
                control={control}
                rows={5}
              />
            </Grid>
          </Grid>
        </Stack>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Stack direction="column" spacing={1} justifyContent="space-between" height="100%">
          <Stack direction="column" spacing={1}>
            <Typography variant="p4" color="primary.main">
              {t('screens.cs.carrier_quotes.quote_offer.contact')}
            </Typography>
            <KNFormText
              name="carrierContact"
              label={t('screens.cs.carrier_quotes.quote_offer.name')}
              control={control}
              rules={{
                required: t('form.validation.required'),
              }}
            />
            <KNFormText
              name="carrierContactEmail"
              label={t('screens.cs.carrier_quotes.quote_offer.email')}
              control={control}
              type="email"
              rules={{
                required: t('form.validation.required'),
                validate: (value) => {
                  if (!validateEmail(value.toString())) {
                    return t('form.validation.invalid_email')
                  }
                  return true
                },
              }}
            />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              color="secondary"
              variant="outlined"
              fullWidth
              loading={sendQuoteAnswerMutation.isPending}
              onClick={() => {
                flushSync(() => setPendingAction(QuoteStatus.REJECTED))
                handleSubmit(onReject)()
              }}
            >
              {t('general.reject')}
            </Button>
            <Button
              color="secondary"
              variant="contained"
              fullWidth
              loading={sendQuoteAnswerMutation.isPending}
              onClick={() => {
                flushSync(() => setPendingAction(QuoteStatus.SUBMITTED))
                handleSubmit(onSubmit)()
              }}
            >
              {t('general.submit')}
            </Button>
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  )
}

export default CarrierQuoteForm

